import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "@phosphor-icons/react";

const COUNTDOWN_SECONDS = 3;
const RADIUS = 15;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// Netflix-style "Up Next" prompt — gives the user a beat to notice and cancel
// the auto-advance instead of silently jumping to the next lesson/episode.
const UpNextOverlay = ({ nextItem, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [ringActive, setRingActive] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setRingActive(true));
    const tick = setInterval(() => {
      setSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    const done = setTimeout(onConfirm, COUNTDOWN_SECONDS * 1000);

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(tick);
      clearTimeout(done);
    };
  }, [onConfirm]);

  return (
    <div className="absolute inset-0 z-20 flex items-end justify-end p-4 sm:p-6 bg-black/40">
      <div className="w-[280px] sm:w-[320px] bg-gradient-to-br from-grey to-grey/50 border border-gray-800 rounded-2xl shadow-xl p-4 flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className="relative shrink-0 w-20 h-14 rounded-lg overflow-hidden bg-black">
            {nextItem?.thumbnail && (
              <img
                src={nextItem.thumbnail}
                alt=""
                className="w-full h-full object-cover"
              />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-white/60 text-xs mb-1">
              {t("video.up_next") || "Up next"}
            </p>
            <h4 className="text-white text-sm font-medium line-clamp-2">
              {nextItem?.title}
            </h4>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="flex-1 h-11 rounded-lg border border-white/10 text-white/80 text-sm font-medium hover:bg-white/5 transition-colors flex items-center justify-center gap-1.5"
          >
            <X size={16} />
            {t("general.cancel") || "Cancel"}
          </button>

          <button
            onClick={onConfirm}
            className="flex-1 h-11 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
          >
            <span className="relative w-6 h-6 shrink-0 flex items-center justify-center">
              <svg className="absolute inset-0 -rotate-90" viewBox="0 0 36 36">
                <circle
                  cx="18"
                  cy="18"
                  r={RADIUS}
                  fill="none"
                  stroke="rgba(255,255,255,0.25)"
                  strokeWidth="3"
                />
                <circle
                  cx="18"
                  cy="18"
                  r={RADIUS}
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={CIRCUMFERENCE}
                  strokeDashoffset={ringActive ? 0 : CIRCUMFERENCE}
                  className="transition-[stroke-dashoffset] ease-linear motion-reduce:transition-none"
                  style={{ transitionDuration: `${COUNTDOWN_SECONDS}s` }}
                />
              </svg>
              <span className="text-[10px] font-semibold leading-none">
                {secondsLeft}
              </span>
            </span>
            {t("video.play_now") || "Play Now"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpNextOverlay;
